import { useCallback, useEffect, useState } from "react";
import { lumiFetch } from "./lumiApi";
import { toast } from "./lumiToast";

// Lumi admin — pending organisation verifications requiring manual review.
// Louise can approve (auto-emails the applicant a continuation link that
// carries a verify_token) or reject with a note.

const STATUS_STYLE = {
  code_sent: "bg-neutral-100 text-neutral-700 border-neutral-200",
  verified: "bg-emerald-100 text-emerald-800 border-emerald-200",
  manual_review_required: "bg-amber-100 text-amber-800 border-amber-200",
  manual_approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  manual_rejected: "bg-rose-100 text-rose-800 border-rose-200",
  expired: "bg-neutral-200 text-neutral-500 border-neutral-300",
};

export default function VerificationsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("");
  const [statusFilter, setStatusFilter] = useState("manual_review_required");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (sector) qs.set("sector", sector);
      if (statusFilter) qs.set("status_filter", statusFilter);
      const r = await lumiFetch(`/verify/list${qs.toString() ? `?${qs.toString()}` : ""}`);
      setItems(r.verifications || []);
    } catch (e) {
      setError(e?.data?.detail || "Could not load verifications");
    } finally {
      setLoading(false);
    }
  }, [q, sector, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (v) => {
    const note = prompt(`Approve verification for "${v.org_name}"?\nOptional note:`, "");
    if (note === null) return;
    try {
      const r = await lumiFetch(`/verify/approve`, {
        method: "POST",
        body: JSON.stringify({ verify_id: v.verify_id, note }),
      });
      if (r?.email_sent === false) {
        toast.error(`Approved, but email failed to send: ${r.email_error || "unknown"}`);
      } else {
        toast.success("Approved — email sent");
      }
      await load();
    } catch (e) {
      // Never let a network / render error crash the whole Lumi page.
      console.error("[VERIFY] approve failed", e);
      toast.error(e?.data?.detail || e?.message || "Approve failed");
    }
  };

  const reject = async (v) => {
    const note = prompt(`Reject verification for "${v.org_name}"?\nOptional note:`, "");
    if (note === null) return;
    try {
      await lumiFetch(`/verify/reject`, {
        method: "POST",
        body: JSON.stringify({ verify_id: v.verify_id, note }),
      });
      toast.success("Rejected");
      await load();
    } catch (e) {
      console.error("[VERIFY] reject failed", e);
      toast.error(e?.data?.detail || e?.message || "Reject failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="lumi-verifications-tab">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-xl font-bold">Organisation Verifications</h2>
        <div className="text-xs text-neutral-500">{items.length} shown</div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="px-3 py-2 rounded-lg border border-neutral-300 text-sm min-w-[220px] flex-1" data-testid="lumi-verify-search" />
        <select value={sector} onChange={(e) => setSector(e.target.value)} className="px-3 py-2 rounded-lg border border-neutral-300 text-sm" data-testid="lumi-verify-sector">
          <option value="">All sectors</option>
          <option value="hotel">Hotels</option>
          <option value="school">Schools</option>
          <option value="charity">Charities</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-neutral-300 text-sm" data-testid="lumi-verify-status">
          <option value="">All statuses</option>
          <option value="manual_review_required">Pending manual review</option>
          <option value="code_sent">Code sent (waiting)</option>
          <option value="verified">Auto-verified</option>
          <option value="manual_approved">Manually approved</option>
          <option value="manual_rejected">Rejected</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3 text-sm">{error}</div>}
      {loading && <div className="text-sm text-neutral-500">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 rounded-xl p-8 text-center">No verifications match those filters.</div>
      )}

      <div className="space-y-2">
        {items.map((v) => (
          <div key={v.verify_id} className="border border-neutral-200 rounded-xl bg-white p-4" data-testid={`lumi-verify-row-${v.verify_id}`}>
            <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
              <p className="font-semibold text-neutral-900">{v.org_name}</p>
              <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_STYLE[v.status] || STATUS_STYLE.code_sent}`}>{(v.status || "unknown").replace(/_/g, " ")}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <Cell label="Sector" value={v.sector} />
              <Cell label="Email" value={v.email} />
              <Cell label="Client IP" value={v.client_ip} mono />
              <Cell label="AI eligible" value={String(v?.ai?.eligible)} />
              <Cell label="AI confidence" value={typeof v?.ai?.confidence === "number" ? v.ai.confidence.toFixed(2) : "—"} />
              <Cell label="AI evidence type" value={v?.ai?.evidence_type} />
              <div className="md:col-span-3">
                <p className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">AI reason</p>
                <p className="text-xs text-neutral-700 bg-neutral-50 rounded-lg p-2">{v?.ai?.reason || "—"}</p>
              </div>
              {v?.ai?.evidence_url && (
                <div className="md:col-span-3">
                  <p className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Evidence URL</p>
                  <a href={v.ai.evidence_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 underline break-all">{v.ai.evidence_url}</a>
                  {v?.evidence_probe && (
                    <p className="text-[10px] text-neutral-500 mt-1">
                      HTTP {v.evidence_probe.http_status || "—"} · host {v.evidence_probe.host || "—"} · matches email domain: {String(v.evidence_probe.matches_domain)} · trusted register: {String(v.evidence_probe.on_trusted_register)}
                    </p>
                  )}
                </div>
              )}
              {v.manual_note && <div className="md:col-span-3"><p className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Admin note</p><p className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2">{v.manual_note}</p></div>}
            </div>

            {(v.status === "manual_review_required" || v.status === "code_sent") && (
              <div className="flex gap-2 mt-3">
                <button onClick={() => approve(v)} data-testid={`lumi-verify-approve-${v.verify_id}`} className="text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700">Approve manually</button>
                <button onClick={() => reject(v)} data-testid={`lumi-verify-reject-${v.verify_id}`} className="text-xs px-3 py-1.5 rounded-full bg-rose-100 text-rose-800 hover:bg-rose-200">Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Cell({ label, value, mono }) {
  return (
    <div className="rounded-lg p-2 bg-white border border-neutral-200">
      <p className="text-[10px] uppercase tracking-wide text-neutral-500">{label}</p>
      <p className={`text-xs ${mono ? "font-mono" : ""} break-all`}>{value || "—"}</p>
    </div>
  );
}
